/**
 * Journal colour accents.
 *
 * Warm, non-clinical tints drawn from the existing Recovery Trauma™ token
 * palette so every journal reads as its own place — especially on a phone,
 * where a wall of identical cream cards is hard to move through.
 * Each accent has a light-and-dark-aware tint (surface) and ink (text/icon).
 */

export type JournalAccent = { tint: string; ink: string; name: string };

const accent = (name: string): JournalAccent => ({
  name,
  tint: `var(--qa-${name}-tint)`,
  ink: `var(--qa-${name}-ink)`,
});

export const ACCENTS: Record<string, JournalAccent> = {
  gold: accent("gold"),
  peach: accent("peach"),
  rose: accent("rose"),
  teal: accent("teal"),
  mint: accent("mint"),
  indigo: accent("indigo"),
  lavender: accent("lavender"),
  plum: accent("plum"),
};

const ORDER = ["gold", "peach", "teal", "rose", "mint", "indigo", "lavender", "plum"];

/** A stable accent for any index — used for tabs, chapters and lists. */
export function accentAt(index: number): JournalAccent {
  return ACCENTS[ORDER[index % ORDER.length]!]!;
}

/** Accent per journal route, so a journal keeps the same colour everywhere. */
export const JOURNAL_ACCENTS: Record<string, JournalAccent> = {
  "/journal": ACCENTS.gold!,
  "/guided-journal": ACCENTS.peach!,
  "/trigger-journal": ACCENTS.rose!,
  "/music-journal": ACCENTS.indigo!,
  "/inner-child": ACCENTS.peach!,
  "/shadow-journey": ACCENTS.plum!,
  "/shadow-work": ACCENTS.lavender!,
  "/abuse-journal": ACCENTS.teal!,
  "/dissociation-journal": ACCENTS.mint!,
  "/sleep-dreams": ACCENTS.indigo!,
  "/dreams": ACCENTS.indigo!,
};

export function journalAccent(route: string): JournalAccent {
  return JOURNAL_ACCENTS[route] ?? ACCENTS.gold!;
}

/** Inline style for a tinted surface with matching ink. */
export function accentSurface(a: JournalAccent): React.CSSProperties {
  return { backgroundColor: a.tint, color: a.ink };
}
